// src/pages/PortalAluno.tsx
// ============================================================================
// PORTAL DO ALUNO — PREMIUM V7.1 · Sidebar Azul Escuro/Dourado
// ============================================================================
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  LayoutDashboard, CreditCard, FileText, Award,
  User, ShoppingCart, LogOut, Menu, X,
  Home, ChevronRight, Bell, Settings,
  Newspaper,
} from 'lucide-react';

import DashboardAluno        from '../components/PortalAluno/DashboardAluno';
import CarteiraDigital       from '../components/PortalAluno/CarteiraDigital';
import VisualizadorCertificado from '../components/PortalAluno/VisualizadorCertificado';
import SolicitadorDiploma    from '../components/PortalAluno/SolicitadorDiploma';
import MeusDados             from '../components/PortalAluno/MeusDados';
import CheckoutDashboard     from '../components/PortalAluno/CheckoutDashboard';
import CarreiraProfissional  from '../components/PortalAluno/CarreiraProfissional';
import FeedNovidades from '../components/shared/FeedNovidades';

interface RootState {
  auth: { user: { name: string; email: string } | null };
}

type TabType =
  | 'dashboard' | 'carteira' | 'certificados'
  | 'diploma'   | 'dados'    | 'cursos' | 'carreira' | 'novidades';

// ── Design tokens ─────────────────────────────────────────────────────────
// 🔵 Alterado: chumbo (#2b2b2e) → azul escuro navy (#0f1b2e)
const SB_BG     = '#0f1b2e';
const SB_ONLINE = '#0f1b2e';

const PortalAluno: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const [activeTab,   setActiveTab]   = useState<TabType>('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const menuItems = useMemo(() => [
    { id:'dashboard'   as TabType, label:'Início',           icon: Home,           img:'/icons/icon-dashboard.png',   desc:'Visão geral'          },
    { id:'carteira'    as TabType, label:'Carteira Digital', icon: CreditCard,     img:'/icons/icon-carteira.png',    desc:'QR Code + Foto'       },
    { id:'certificados'as TabType, label:'Certificados',     icon: Award,          img:'/icons/icon-certificados.png',desc:'Baixar e validar'     },
    { id:'cursos'      as TabType, label:'Comprar Cursos',   icon: ShoppingCart,   img:'/icons/icon-cursos.png',      desc:'Checkout interno'     },
    { id:'carreira'    as TabType, label:'Carreira',         icon: LayoutDashboard,img:'/icons/icon-carreira.png',    desc:'Evolução profissional'},
    { id:'dados'       as TabType, label:'Meus Dados',       icon: User,           img:'/icons/icon-dados.png',       desc:'Cadastro e perfil'    },
    { id:'novidades'   as TabType, label:'Novidades',        icon: Newspaper,      img:'/icons/icon-novidades.png',    desc:'Atualizações e novidades' },
  ], []);

  const activeItem = useMemo(() =>
    menuItems.find(i => i.id === activeTab) ?? menuItems[0],
    [activeTab, menuItems]
  );

  const initials = user?.name
    ?.split(' ').slice(0,2).map(n => n[0]).join('').toUpperCase() || 'AL';

  const handleLogout = () => navigate('/');

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':    return <DashboardAluno />;
      case 'carteira':     return <CarteiraDigital />;
      case 'certificados': return <VisualizadorCertificado />;
      case 'dados':        return <MeusDados />;
      case 'cursos':       return <CheckoutDashboard />;
      case 'carreira':     return <CarreiraProfissional />;
      case 'novidades':    return <FeedNovidades />;
      default:             return <DashboardAluno />;
    }
  };

  // ── Glow edge right ───────────────────────────────────────────────────
  const GoldEdge = () => (
    <div className="absolute right-0 top-0 bottom-0 w-[1px]
      bg-gradient-to-b from-transparent via-gold-600/22 to-transparent pointer-events-none" />
  );

  const TextureOverlay = () => (
    <div className="absolute inset-0 pointer-events-none" style={{
      background:`radial-gradient(ellipse 80% 40% at 50% 0%, rgba(212,175,55,0.06) 0%, transparent 70%),
                 radial-gradient(ellipse 60% 30% at 50% 100%, rgba(0,0,0,0.25) 0%, transparent 70%)`
    }} />
  );

  // ── Nav ──────────────────────────────────────────────────────────────
  const SidebarNav = ({ onSelect }: { onSelect?: () => void }) => (
    <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
      {menuItems.map((item) => {
        const Icon   = item.icon;
        const active = activeTab === item.id;
        return (
          <button key={item.id}
            onClick={() => { setActiveTab(item.id); onSelect?.(); }}
            className={`
              relative w-full group flex items-center gap-3 px-3 py-2.5 rounded-xl
              text-sm font-medium transition-all duration-200 overflow-hidden
              ${active
                ? 'text-[#1a1a1c]'
                : 'text-[#a0a0a8] hover:text-gold-300 hover:bg-white/[0.05]'}
            `}
          >
            {active && (
              <>
                <span className="absolute inset-0 rounded-xl bg-gradient-to-r from-gold-400 via-gold-500 to-gold-600" />
                <span className="absolute inset-0 rounded-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.28),0_4px_20px_rgba(212,175,55,0.40)]" />
              </>
            )}
            {!active && (
              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-0
                group-hover:h-7 rounded-r-full bg-gold-500/50 transition-all duration-200" />
            )}

            <span className={`
              relative z-10 flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0
              transition-all duration-200
              ${active ? 'bg-black/[0.10]' : 'bg-white/[0.06] group-hover:bg-gold-500/12'}
            `}>
              <img src={item.img} alt={item.label}
                className="w-5 h-5 object-contain"
                onError={e => {
                  const el = e.currentTarget as HTMLImageElement;
                  el.style.display = 'none';
                  (el.nextElementSibling as HTMLElement)?.classList.remove('hidden');
                }}
              />
              <Icon className={`hidden w-[17px] h-[17px]
                ${active ? 'text-[#1a1a1c]' : 'text-[#a0a0a8] group-hover:text-gold-400'}`} />
            </span>

            <div className="relative z-10 flex-1 text-left min-w-0">
              <p className="leading-none">{item.label}</p>
              <p className={`text-[10px] mt-0.5 truncate
                ${active ? 'text-[#1a1a1c]/55' : 'text-[#5a5a62] group-hover:text-[#7a7a82]'}`}>
                {item.desc}
              </p>
            </div>

            {active && (
              <ChevronRight className="relative z-10 w-3.5 h-3.5 opacity-35 flex-shrink-0" />
            )}
          </button>
        );
      })}
    </nav>
  );

  // ── Sidebar inner ────────────────────────────────────────────────────
  const SidebarInner = ({ onSelect, showClose }: { onSelect?: () => void; showClose?: boolean }) => (
    <>
      {/* Logo */}
      <div className="relative px-6 py-5 border-b border-white/[0.07]">
        <div className="absolute top-0 left-6 right-6 h-[1px]
          bg-gradient-to-r from-transparent via-gold-500/55 to-transparent" />
        <div className="flex items-center gap-3">
          <div className="relative flex-shrink-0">
            <div className="absolute inset-0 rounded-full bg-gold-400/20 blur-[10px]" />
            <img src="/imagem/Bras.png" alt="FEPCSSP"
              className="relative w-11 h-11 object-contain
                drop-shadow-[0_2px_8px_rgba(212,175,55,0.45)]" />
          </div>
          <div className="flex-1">
            <p className="text-[15px] font-bold tracking-[0.18em] text-gold-400 leading-none">
              FEPCSSP
            </p>
            <p className="text-[9.5px] text-[#7a7a82] uppercase tracking-[0.22em] mt-1 font-medium">
              Portal do Aluno
            </p>
          </div>
          <span className="text-[8.5px] font-bold tracking-widest text-gold-500
            bg-gold-500/10 border border-gold-500/22 px-2 py-0.5 rounded-md uppercase leading-none">
            PRO
          </span>
          {showClose && (
            <button onClick={() => setSidebarOpen(false)}
              className="ml-1 p-1.5 hover:bg-white/10 rounded-lg text-[#7a7a82] transition-colors">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* User card */}
      <div className="px-4 py-3 mx-3 mt-4 mb-2 rounded-2xl
        bg-white/[0.04] border border-gold-500/[0.13]">
        <div className="flex items-center gap-3">
          <div className="relative flex-shrink-0">
            <div className="absolute inset-0 rounded-full bg-gold-500/25 blur-[8px]" />
            <div
              className="relative w-10 h-10 rounded-full flex items-center justify-center
                text-[#1a1a1c] font-semibold text-sm
                ring-2 ring-gold-500/28 shadow-[0_0_12px_rgba(212,175,55,0.30)]"
              style={{ background:'linear-gradient(135deg,#f4d03f,#b8860b)' }}
            >
              {initials}
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full
              shadow-[0_0_6px_rgba(37,211,102,0.55)]"
              style={{ border:`2px solid ${SB_ONLINE}` }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium text-white/85 truncate leading-tight">
              {user?.name || 'Aluno'}
            </p>
            <p className="text-[10.5px] text-[#7a7a82] truncate mt-0.5 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 bg-gold-500/65 rounded-full flex-shrink-0" />
              {user?.email || 'email@exemplo.com'}
            </p>
          </div>
        </div>
      </div>

      {/* Label */}
      <div className="px-7 mb-2 mt-1">
        <p className="text-[9px] font-semibold tracking-[0.22em] text-[#5a5a62] uppercase">
          Navegação
        </p>
      </div>

      <SidebarNav onSelect={onSelect} />

      {/* Logout */}
      <div className="px-3 pb-5 pt-2">
        <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent mb-3" />
        <button onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl
            text-sm font-medium text-[#7a7a82]
            hover:bg-red-500/10 hover:text-red-400 transition-all group">
          <span className="w-8 h-8 rounded-lg bg-white/[0.05] group-hover:bg-red-500/15
            flex items-center justify-center transition-colors flex-shrink-0">
            <LogOut className="w-4 h-4" />
          </span>
          Sair da conta
        </button>
      </div>
    </>
  );

  const sidebarShell = `flex flex-col relative border-r border-white/[0.06]`;

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">

      {/* ── SIDEBAR DESKTOP ─────────────────────────────────────────── */}
      <aside className={`hidden lg:flex w-[280px] flex-shrink-0 ${sidebarShell}`}
        style={{ backgroundColor: SB_BG }}>
        <TextureOverlay />
        <GoldEdge />
        <SidebarInner />
      </aside>

      {/* ── SIDEBAR MOBILE ──────────────────────────────────────────── */}
      {sidebarOpen && (
        <>
          <div className="fixed inset-0 bg-black/75 backdrop-blur-[3px] z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)} />
          <aside className={`fixed inset-y-0 left-0 w-72 z-50 lg:hidden
            shadow-[4px_0_40px_rgba(0,0,0,0.5)] ${sidebarShell}`}
            style={{ backgroundColor: SB_BG }}>
            <TextureOverlay />
            <GoldEdge />
            <SidebarInner onSelect={() => setSidebarOpen(false)} showClose />
          </aside>
        </>
      )}

      {/* ── MAIN ────────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">

        {/* Mobile header */}
        <header className="relative lg:hidden flex items-center justify-between
          px-4 py-3.5 border-b border-white/[0.07]
          shadow-[0_4px_20px_rgba(0,0,0,0.35)] flex-shrink-0"
          style={{ backgroundColor: SB_BG }}>
          <div className="absolute bottom-0 left-0 right-0 h-[1px]
            bg-gradient-to-r from-transparent via-gold-600/28 to-transparent" />
          <button onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-white/10 rounded-xl text-gold-400 transition-colors">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <div className="absolute inset-0 bg-gold-400/20 blur-md rounded-full" />
              <img src="/imagem/Bras.png" alt=""
                className="relative w-7 h-7 object-contain" />
            </div>
            <span className="font-bold text-gold-400 tracking-widest text-sm">FEPCSSP</span>
          </div>
          <button className="relative p-2 hover:bg-white/10 rounded-xl text-[#7a7a82] transition-colors">
            <Bell className="w-5 h-5" />
            <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full
              shadow-[0_0_6px_rgba(239,68,68,0.65)]"
              style={{ border:`2px solid ${SB_BG}` }} />
          </button>
        </header>

        {/* Desktop header */}
        <header className="relative hidden lg:flex items-center justify-between
          px-8 py-4 bg-white border-b border-gray-100 flex-shrink-0
          shadow-[0_1px_12px_rgba(0,0,0,0.06)]">
          <div className="absolute top-0 left-0 right-0 h-[2px]
            bg-gradient-to-r from-transparent via-gold-500/30 to-transparent" />

          <div>
            <h2 className="text-xl font-semibold text-gray-900 tracking-tight">
              {activeItem.label}
            </h2>
            <p className="text-[11px] text-gray-400 mt-0.5 flex items-center gap-1.5 tracking-wide">
              <span className="w-1.5 h-1.5 rounded-full bg-gold-500 inline-block" />
              {activeItem.desc}
            </p>
          </div>

          <div className="flex items-center gap-1">
            <button className="relative p-2.5 hover:bg-gray-50 rounded-xl transition-colors group">
              <Bell className="w-5 h-5 text-gray-400 group-hover:text-gray-700 transition-colors" />
              <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full
                ring-2 ring-white shadow-[0_0_6px_rgba(239,68,68,0.55)]" />
            </button>
            <button className="p-2.5 hover:bg-gray-50 rounded-xl transition-colors group">
              <Settings className="w-5 h-5 text-gray-400 group-hover:text-gray-700 transition-colors" />
            </button>
            <div className="w-px h-6 bg-gray-200 mx-2" />
            <div className="flex items-center gap-2.5 pl-0.5 pr-3 py-1.5 rounded-xl
              bg-gray-50 border border-gray-100 hover:border-gold-200 transition-colors cursor-default">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-gold-400/20 blur-[6px]" />
                <div className="relative w-8 h-8 rounded-full flex items-center justify-center
                  text-[#1a1a1c] font-semibold text-xs ring-2 ring-gold-200 shadow-gold"
                  style={{ background:'linear-gradient(135deg,#f4d03f,#b8860b)' }}>
                  {initials}
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500
                  rounded-full border-2 border-white" />
              </div>
              <div className="hidden xl:block leading-tight">
                <p className="text-[13px] font-medium text-gray-800">
                  {user?.name?.split(' ')[0] || 'Aluno'}
                </p>
                <p className="text-[10px] text-gray-400">Acesso ativo</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 px-4 py-6 md:px-8 md:py-8">
          {renderContent()}
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-100 px-6 py-3 text-center flex-shrink-0">
          <p className="text-[11px] text-gray-400 tracking-wide">
            Federação dos Profissionais da Beleza
            <span className="mx-2 text-gray-200">·</span>
            <span className="text-gold-600 font-medium">Área restrita do Aluno</span>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default PortalAluno;
